import * as React from "react";
import { styled } from "@mui/material/styles";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import CloseIcon from "@mui/icons-material/Close";
import Heading from "../../../component-lib/Atoms/heading/EventEHRHeading";
import * as Yup from "yup";
import { Form, Formik } from "formik";
import moment from "moment";
import { VALIDATION_CONSTANTS } from "../../../utils/validation-constants/validationConstants";
import Input from "../../../component-lib/Atoms/Input/Input";
import Button from "../../../component-lib/Molecules/button/Button";
import { TEXT_CONSTANTS } from "../../../utils/text-constants/textConstants";
import SelectDropdown from "../../../component-lib/Atoms/Select/Select";
import DatePicker from "../../../component-lib/Atoms/DatePicker/DatePicker";
// ROLE_BASED_REMOVED (pre-Jamboree): `roleBasedOptions` import dropped.
import { assignToCategoryOptions, STATUS_FOR_TASK } from "../constant";
import EventEHRLazyDropdown from "../../../component-lib/Molecules/lazy-loading-search/EventEHRLazyLoadingSearch";
import SettingsService from "../../../services/SettingsService";
import { useDispatch, useSelector } from "react-redux";
import {
  hideLoader,
  showLoader,
} from "../../../component-lib/Molecules/loader/LoaderSlice";
import { getAssignToList, getTaskTypeList } from "../taskSaga";
import {
  getLazyLoadingApiResponse,
  getLazyLoadingSearchApiResponse,
} from "../../../utils/constantsFunctions";

// ADD TASK FORM

export const ADD_TASK_DETAILS = {
  TASK_TYPE: "taskType",
  TASK_TITLE: "taskTitle",
  PATIENT_NAME: "patientFirstName",
  ASSIGN_TO: "assignTo",
  STATUS: "status",
  TASK_DESCRIPTION: "taskDescription",
  DUE_DATE: "dueDate",
};

interface AddTaskProps {
  defaultSelectedData?: any;
  handleOnSubmittedData?: (formData: any) => void;
  handleClose?: () => void;
}

const AddTask = ({
  defaultSelectedData,
  handleOnSubmittedData,
  handleClose,
}: AddTaskProps) => {
  const dispatch = useDispatch();

  // GET PATIENT LOGIC START: THIS USE STATES FOR GET PATIENT--------------
  const [pageCountForGetPatient, setPageCountForGetPatientList] =
    React.useState(1);
  const [dropdownSearchForGetPatient, setDropdownSearchForGetPatient] =
    React.useState("");
  const [dropdownOptionsForGetPatient, setDropdownOptionsForGetPatient] =
    React.useState<any>([]);
  const [selectedValueForGetPatient, setSelectedValueForGetPatient] =
    React.useState<any>(null);
  const [resetFlagForGetPatient, setResetFlagForGetPatient] =
    React.useState(false);

  // GET PATIENT LOGIC END --------------------

  // GET ASSIGN TO LOGIC START: THIS USE STATES FOR GET ASSIGN TO DATA--------------
  const [pageCountForGetAssignTo, setPageCountForAssignTo] = React.useState(1);
  const [dropdownSearchForAssignTo, setDropdownSearchForAssignTo] =
    React.useState("");
  const [dropdownOptionsForAssignTo, setDropdownOptionsForAssignTo] =
    React.useState<any>([]);
  const [selectedValueForAssignTo, setSelectedValueForAssignTo] =
    React.useState<any>(null);
  const [resetFlagForAssignTo, setResetFlagForAssignTo] = React.useState(false);
  const [totalUsersCount, setTotalUsersCount] = React.useState(0);
  const [hasMoreUsers, setHasMoreUsers] = React.useState(true);

  // GET GROUPS LOGIC START: THIS USE STATES FOR GET GROUPS DATA--------------
  const [pageCountForGetGroups, setPageCountForGetGroups] = React.useState(1);
  const [dropdownSearchForGetGroups, setDropdownSearchForGetGroups] =
    React.useState("");
  const [dropdownOptionsForGetGroups, setDropdownOptionsForGetGroups] =
    React.useState<any>([]);
  const [resetFlagForGetGroups, setResetFlagForGetGroups] =
    React.useState(false);
  const [totalGroupsCount, setTotalGroupsCount] = React.useState(0);
  const [hasMoreGroups, setHasMoreGroups] = React.useState(true);

  const [timer, setTimer] = React.useState<NodeJS.Timeout | null>(null);

  // ASSIGN TO CATEGORY LOGIC START: NEW STATE FOR CATEGORY SELECTION
  const [assignToCategory, setAssignToCategory] = React.useState<string>("");
  const [selectedAssignToValue, setSelectedAssignToValue] =
    React.useState<any>(null);

  // GET ASSIGN TO LIST LOGIC START: NEW STATE FOR ASSIGN TO LIST API
  const [pageCountForAssignToList, setPageCountForAssignToList] =
    React.useState(1);
  const [dropdownSearchForAssignToList, setDropdownSearchForAssignToList] =
    React.useState("");
  const [dropdownOptionsForAssignToList, setDropdownOptionsForAssignToList] =
    React.useState<any>([]);
  const [resetFlagForAssignToList, setResetFlagForAssignToList] =
    React.useState(false);
  const [totalAssignToListCount, setTotalAssignToListCount] = React.useState(0);
  const [hasMoreAssignToList, setHasMoreAssignToList] = React.useState(true);
  // Previous assign-to search term, so we can tell "search cleared" (needs a
  // refetch of the unfiltered list) apart from "never searched" (initial
  // mount — the category effect already loaded page 1).
  const prevAssignToSearch = React.useRef("");

  // Get assign to list data from Redux store
  const { assignToListData, taskTypeListData } = useSelector(
    (state: any) => state.TASK_SLICE
  );

  // TASK TYPE OPTIONS: fetched from master data (type = task_type).
  // Uses the task type name for both label and value (not id).
  const taskTypeOptions = (taskTypeListData ?? [])
    .filter((item: any) => item?.name)
    .map((item: any) => ({ label: item.name, value: item.name }));

  // Task Type server-search (searchValue) over master data; falls back to the
  // full browse list (taskTypeOptions) when the search box is empty.
  const [taskTypeSearch, setTaskTypeSearch] = React.useState("");
  const [taskTypeSearchResults, setTaskTypeSearchResults] =
    React.useState<any>(null);
  const [taskTypeTimer, setTaskTypeTimer] =
    React.useState<NodeJS.Timeout | null>(null);

  const searchTaskTypes = async (search: string) => {
    try {
      const res = await SettingsService.getMasterDataList({
        type: "task_type",
        searchValue: search,
      });
      const rows = res?.data?.masterData ?? [];
      setTaskTypeSearchResults(
        rows
          .filter((r: any) => r?.name)
          .map((r: any) => ({ label: r.name, value: r.name }))
      );
    } catch {
      setTaskTypeSearchResults([]);
    }
  };

  const handleTaskTypeSearch = (val: string) => {
    if (taskTypeTimer) clearTimeout(taskTypeTimer);
    setTaskTypeSearch(val);
    if (!val) {
      setTaskTypeSearchResults(null);
      return;
    }
    const t = setTimeout(() => searchTaskTypes(val), 500);
    setTaskTypeTimer(t);
  };

  const shownTaskTypeOptions =
    taskTypeSearch && taskTypeSearchResults
      ? taskTypeSearchResults
      : taskTypeOptions;

  // Fetch task type master data on mount
  React.useEffect(() => {
    dispatch(getTaskTypeList());
  }, []);

  // Simple flag to prevent multiple initial API calls
  const initialLoadComplete = React.useRef(false);

  // Handle assign to list data from API response
  React.useEffect(() => {
    if (assignToListData && Object?.keys(assignToListData)?.length > 0) {
      // Discard responses that no longer match the current UI state. Selecting
      // a category and typing a search fire two overlapping requests; without
      // this the unfiltered page-1 response would land last and clobber (or be
      // appended to) the search results.
      const meta = assignToListData?.requestMeta;
      if (!meta) return;
      if (meta?.type !== assignToCategory) return;
      if ((meta?.searchKey || "") !== (dropdownSearchForAssignToList || ""))
        return;

      // Page 1 replaces the list; page 2+ appends to it.
      const isFirstPage = (meta?.pageNumber ?? 1) <= 1;

      const listData = assignToListData?.data?.records || [];
      const total = assignToListData?.data?.totalRecords || 0;

      setTotalAssignToListCount(total);
      const currentLoadedCount = isFirstPage
        ? 0
        : dropdownOptionsForAssignToList?.length;
      setHasMoreAssignToList(currentLoadedCount + listData.length < total);

      const mappedData = listData?.map((item: any) => {
        // Handle different data structures based on category
        let label = "";
        if (assignToCategory === "individuals") {
          // For individuals: firstName + lastName
          const fullName =
            `${item?.firstName || ""} ${item?.lastName || ""}`.trim();
          label = fullName || item?.name || "";
        } else if (assignToCategory === "groups") {
          // For groups: name field
          label = item?.name || "";
        }
        // ROLE_BASED_REMOVED (pre-Jamboree):
        // else if (assignToCategory === "role_based") {
        //   label = item?.name || "";
        // }

        return {
          label: label,
          value: `${item?.id}`,
          key: `${assignToCategory}-${item?.id}`, // Unique key for each category
        };
      });

      if (isFirstPage) {
        // Replace. Runs even when empty so a no-result search clears stale
        // options instead of leaving the previous list on screen.
        setDropdownOptionsForAssignToList(mappedData || []);
        if (resetFlagForAssignToList) {
          setResetFlagForAssignToList(false);
        }
      } else {
        if (mappedData && mappedData?.length > 0) {
          // Page 2+: append, skipping anything already loaded.
          const existingValues = dropdownOptionsForAssignToList?.map(
            (item: any) => item?.value,
          );
          const newData = mappedData?.filter(
            (item: any) => !existingValues?.includes(item?.value),
          );

          if (newData?.length > 0) {
            setDropdownOptionsForAssignToList((prev: any[]) => [
              ...prev,
              ...newData,
            ]);
          }
        }
      }
    }
  }, [
    assignToListData,
    resetFlagForAssignToList,
    assignToCategory,
    dropdownSearchForAssignToList,
  ]);

  // Call assign to list API when category changes
  React.useEffect(() => {
    if (assignToCategory) {
      const typeMapping = {
        individuals: "individuals",
        groups: "groups",
        // ROLE_BASED_REMOVED (pre-Jamboree):
        // role_based: "role_based",
      };

      const payload = {
        type: typeMapping[assignToCategory as keyof typeof typeMapping],
        pageNumber: 1,
        limit: 100,
      };

      // Category change clears the search box; keep the search effect from
      // treating that as a "search cleared" and firing a duplicate fetch.
      prevAssignToSearch.current = "";
      dispatch(getAssignToList(payload));
      setResetFlagForAssignToList(true);
    }
  }, [assignToCategory, dispatch]);

  // Handle search for assign to list
  const handleSearchedValueChangeForAssignToList = (data: any) => {
    if (timer) {
      clearTimeout(timer);
    }
    const newTimer = setTimeout(() => {
      setDropdownSearchForAssignToList(data);
    }, 500);
    setTimer(newTimer);
  };

  // Call API for search, and restore the unfiltered list when it's cleared
  React.useEffect(() => {
    if (!assignToCategory) return;

    const typeMapping = {
      individuals: "individuals",
      groups: "groups",
      // ROLE_BASED_REMOVED (pre-Jamboree):
      // role_based: "role_based",
    };

    const search = dropdownSearchForAssignToList || "";
    const hadSearch = prevAssignToSearch.current.length > 0;
    prevAssignToSearch.current = search;

    // Empty and nothing to clear → initial mount; the category effect already
    // fetched page 1, so don't fetch it twice.
    if (!search && !hadSearch) return;

    const payload: any = {
      type: typeMapping[assignToCategory as keyof typeof typeMapping],
      pageNumber: 1,
      limit: 100,
    };
    if (search) {
      payload.searchKey = search;
    }

    // New search → restart lazy-loading from page 1.
    setPageCountForAssignToList(1);
    dispatch(getAssignToList(payload));
  }, [dropdownSearchForAssignToList, assignToCategory, dispatch]);

  // Handle pagination for assign to list
  React.useEffect(() => {
    if (
      assignToCategory &&
      pageCountForAssignToList > 1 &&
      !resetFlagForAssignToList
    ) {
      const typeMapping = {
        individuals: "individuals",
        groups: "groups",
        // ROLE_BASED_REMOVED (pre-Jamboree):
        // role_based: "role_based",
      };

      const payload: any = {
        type: typeMapping[assignToCategory as keyof typeof typeMapping],
        pageNumber: pageCountForAssignToList,
        limit: 100,
      };
      // Keep paging within the active search, otherwise page 2+ would append
      // unfiltered results on top of the filtered ones.
      if (dropdownSearchForAssignToList) {
        payload.searchKey = dropdownSearchForAssignToList;
      }

      dispatch(getAssignToList(payload));
    }
  }, [
    pageCountForAssignToList,
    assignToCategory,
    resetFlagForAssignToList,
    dispatch,
  ]);

  const validationSchema = Yup.object().shape({
    [ADD_TASK_DETAILS.TASK_TYPE]: Yup.string().required(
      "Task type is required",
    ),

    [ADD_TASK_DETAILS.ASSIGN_TO]: Yup.string().when([], {
      is: () => assignToCategory && assignToCategory?.length > 0,
      then: (schema) => {
        let errorMessage = "Please select a specific assignment";
        if (assignToCategory === "individuals") {
          errorMessage = "Individual is required";
        } else if (assignToCategory === "groups") {
          errorMessage = "Group is required";
        }
        // ROLE_BASED_REMOVED (pre-Jamboree):
        // else if (assignToCategory === "role_based") {
        //   errorMessage = "Role is required";
        // }
        return schema.required(errorMessage);
      },
      otherwise: (schema) => schema.required("Assign to is required"),
    }),

    [ADD_TASK_DETAILS.DUE_DATE]: Yup.date().required("Due date is required"),

    [ADD_TASK_DETAILS.STATUS]: Yup.string()
      .matches(
        VALIDATION_CONSTANTS.NAME,
        "Status must contain only letters and numbers",
      )
      .required("Status is required"),

    [ADD_TASK_DETAILS.TASK_DESCRIPTION]: Yup.string()
      // .matches(
      //   VALIDATION_CONSTANTS.NAME,
      //   "Task description must contain only letters and numbers"
      // )
      .required("Task description is required"),
  });

  const commonGridStyle = {
    padding: "0px 0px 18px 0px",
    display: "grid",
    gap: "24px 16px",
  };

  const fullWidthStyle = {
    gridTemplateColumns: "1fr",
  };

  const twoColumnStyle = {
    gridTemplateColumns: "1fr 1fr",
  };

  // Handle edit/view data mapping
  React.useEffect(() => {
    if (!defaultSelectedData?.status) return;

    setSelectedValueForGetPatient({
      label: defaultSelectedData?.patientName || "",
      value: defaultSelectedData?.rowData?.patientId || "",
    });

    const assignToData = defaultSelectedData?.rowData;

    if (assignToData?.assigendTo) {
      setAssignToCategory("individuals");

      setSelectedAssignToValue({
        label: `${assignToData.assigendTo.firstName || ""} ${
          assignToData.assigendTo.lastName || ""
        }`.trim(),
        value: assignToData.assigendTo.id,
      });
    } else if (assignToData?.assignedToAdminUser) {
      setAssignToCategory("individuals");

      setSelectedAssignToValue({
        label: `${assignToData.assignedToAdminUser.firstName || ""} ${
          assignToData.assignedToAdminUser.lastName || ""
        }`.trim(),
        value: assignToData.assignedToAdminUser.id,
      });
    } else if (assignToData?.group) {
      setAssignToCategory("groups");

      setSelectedAssignToValue({
        label: assignToData.group.name || "",
        value: assignToData.group.id,
      });
    }

    // Only load dropdown when category exists
    if (
      assignToData?.assigendTo ||
      assignToData?.assignedToAdminUser ||
      assignToData?.group
    ) {
      const type = assignToData?.group ? "groups" : "individuals";

      dispatch(
        getAssignToList({
          type,
          pageNumber: 1,
          limit: 100,
        }),
      );

      setResetFlagForAssignToList(true);
    }
  }, [defaultSelectedData, dispatch]);

  // Update form field when assign-to category and value are set during editing
  React.useEffect(() => {
    if (assignToCategory && selectedAssignToValue?.value) {
      // This will be handled by the form's setFieldValue in the dropdown onChange
      // The form will automatically update when the dropdown value changes
    }
  }, [assignToCategory, selectedAssignToValue]);

  // Load initial data only once on component mount
  React.useEffect(() => {
    if (!initialLoadComplete.current) {
      // Load all initial data in one effect to prevent multiple calls
      getDropdownList(1, true); // Load patients
      getDropdownListForAssignTo(1, true); // Load users
      getDropdownListForGroups(1, true); // Load groups
      initialLoadComplete.current = true; // Mark initial load as complete
    }
  }, []); // Empty dependency array - runs only once

  //GET PATIENT LOGIC START:  BELOW USE EFFECT AND FUNCTIONS  WE ARE USING FOR GET PATIENT LIST, SEARCH PATIENT , AND LAZY LOAD PATIENT DATA
  // **************************************************************
  React.useEffect(() => {
    // Only run if initial load is complete and not reset
    if (initialLoadComplete.current && !resetFlagForGetPatient) {
      if (
        dropdownSearchForGetPatient &&
        dropdownSearchForGetPatient.length > 0
      ) {
        getDropdownSearchList(dropdownSearchForGetPatient);
      } else if (pageCountForGetPatient > 1) {
        getDropdownList(pageCountForGetPatient);
      }
    }
  }, [
    dropdownSearchForGetPatient,
    pageCountForGetPatient,
    resetFlagForGetPatient,
  ]);

  React.useEffect(() => {
    if (resetFlagForGetPatient) {
      resetList();
    }
  }, [resetFlagForGetPatient]);

  const resetList = () => {
    setSelectedValueForGetPatient(null);
    setDropdownSearchForGetPatient("");
    setPageCountForGetPatientList(1);
    getDropdownList(1, true);
  };

  // IF SEARCH VALUE  THEN WE ARE GIVEN SOME TIMER
  const handleSearchedValueChange = (data: any) => {
    if (timer) {
      clearTimeout(timer); // Clear the previous timer
    }
    // Set a new timer with your desired delay (e.g., 500ms)
    const newTimer = setTimeout(() => {
      setDropdownSearchForGetPatient(data);
    }, 500); // Adjust the delay as needed (in milliseconds)

    setTimer(newTimer); // Store the timer ID for cleanup
  };

  // GET DROPDOWN LIST
  const getDropdownList = async (pageCount: any, reset = false) => {
    dispatch(showLoader());
    try {
      const response = await getLazyLoadingApiResponse("patient", pageCount, 6);

      let patientList = response?.data?.patient || {};

      const mappedPatientData = patientList?.map((data: any) => ({
        label: `${data?.participant?.firstName} ${data?.participant?.lastName}`,
        value: `${data?.id}`,
      }));

      if (mappedPatientData && mappedPatientData.length > 0) {
        if (reset) {
          setDropdownOptionsForGetPatient(mappedPatientData);
          setResetFlagForGetPatient(false);
        } else if (
          !dropdownOptionsForGetPatient.includes(mappedPatientData[0])
        ) {
          let options = dropdownOptionsForGetPatient.concat(mappedPatientData);
          setDropdownOptionsForGetPatient(options);
        }
      }
    } catch (error) {
      console.log("error:", error);
    } finally {
      dispatch(hideLoader());
    }
  };

  // GET DROPDOWN SEARCH LIST
  const getDropdownSearchList = async (search: string) => {
    try {
      const response = await getLazyLoadingSearchApiResponse("patient", search);
      let patientList = response?.data?.patient || {};
      const mappedPatientData = patientList?.map((data: any) => ({
        label: `${data?.participant?.firstName} ${data?.participant?.lastName}`,
        value: `${data?.id}`,
      }));
      if (mappedPatientData?.length) {
        setDropdownOptionsForGetPatient(mappedPatientData);
      }
    } catch (error) {
      console.log("error:", error);
    }
  };

  // **************************************************************
  //GET PATIENT LOGIC END

  //GET  ASSIGN TO  START:  BELOW USE EFFECT AND FUNCTIONS  WE ARE USING FOR GET ASSIGN TO  LIST, SEARCH ASSIGN TO  , AND LAZY LOAD ASSIGN TO  DATA
  // **************************************************************
  React.useEffect(() => {
    // Only run if initial load is complete and not reset
    if (initialLoadComplete.current && !resetFlagForAssignTo) {
      if (dropdownSearchForAssignTo && dropdownSearchForAssignTo.length > 0) {
        getDropdownSearchListForAssignTo(dropdownSearchForAssignTo);
      } else if (pageCountForGetAssignTo > 1) {
        getDropdownListForAssignTo(pageCountForGetAssignTo);
      }
    }
  }, [
    dropdownSearchForAssignTo,
    pageCountForGetAssignTo,
    resetFlagForAssignTo,
  ]);

  React.useEffect(() => {
    if (resetFlagForAssignTo) {
      resetListForAssignTo();
    }
  }, [resetFlagForAssignTo]);

  const resetListForAssignTo = () => {
    setSelectedValueForAssignTo(null);
    setDropdownSearchForAssignTo("");
    setPageCountForAssignTo(1);
    setTotalUsersCount(0);
    setHasMoreUsers(true);
    getDropdownListForAssignTo(1, true);
  };

  // IF SEARCH VALUE  THEN WE ARE GIVEN SOME TIMER
  const handleSearchedValueChangeForAssignTo = (data: any) => {
    if (timer) {
      clearTimeout(timer); // Clear the previous timer
    }
    // Set a new timer with your desired delay (e.g., 500ms)
    const newTimer = setTimeout(() => {
      setDropdownSearchForAssignTo(data);
    }, 500); // Adjust the delay as needed (in milliseconds)

    setTimer(newTimer); // Store the timer ID for cleanup
  };

  // GET DROPDOWN LIST
  const getDropdownListForAssignTo = async (pageCount: any, reset = false) => {
    // Don't fetch if we've reached the end
    if (!hasMoreUsers && !reset) {
      return;
    }

    dispatch(showLoader());
    try {
      const response = await getLazyLoadingApiResponse("user", pageCount, 6);
      const usersList = response?.data?.users || [];
      const total = response?.data?.total || 0;

      // Update total count and check if we have more data
      setTotalUsersCount(total);
      const currentLoadedCount = reset ? 0 : dropdownOptionsForAssignTo?.length;
      setHasMoreUsers(currentLoadedCount + usersList.length < total);

      const mappedUserData = usersList?.map((data: any) => ({
        label: `${data?.firstName} ${data?.lastName}`,
        value: `${data?.id}`,
      }));

      if (mappedUserData && mappedUserData.length > 0) {
        if (reset) {
          setDropdownOptionsForAssignTo(mappedUserData);
          setResetFlagForAssignTo(false);
        } else if (!dropdownOptionsForAssignTo.includes(mappedUserData[0])) {
          let options = dropdownOptionsForAssignTo.concat(mappedUserData);
          setDropdownOptionsForAssignTo(options);
        }
      }
    } catch (error) {
      console.log("error:", error);
    } finally {
      dispatch(hideLoader());
    }
  };

  // GET DROPDOWN SEARCH LIST
  const getDropdownSearchListForAssignTo = async (search: string) => {
    try {
      const response = await getLazyLoadingSearchApiResponse(
        "user",
        search,
        "name",
      );
      const usersList = response?.data?.users || [];
      const total = response?.data?.total || 0;

      // Reset pagination state for search
      setTotalUsersCount(total);
      setHasMoreUsers(usersList?.length < total);

      const mappedUserData = usersList?.map((data: any) => ({
        label: `${data?.firstName} ${data?.lastName}`,
        value: `${data?.id}`,
      }));
      if (mappedUserData?.length) {
        setDropdownOptionsForAssignTo(mappedUserData);
      }
    } catch (error) {
      console.log("error:", error);
    }
  };

  // **************************************************************
  //GET ASSIGN TO  LOGIC END

  //GET GROUPS LOGIC START: BELOW USE EFFECT AND FUNCTIONS FOR GET GROUPS LIST, SEARCH GROUPS, AND LAZY LOAD GROUPS DATA
  // **************************************************************
  React.useEffect(() => {
    // Only run if initial load is complete and not reset
    if (initialLoadComplete.current && !resetFlagForGetGroups) {
      if (
        dropdownSearchForGetGroups &&
        dropdownSearchForGetGroups?.length > 0
      ) {
        getDropdownSearchListForGroups(dropdownSearchForGetGroups);
      } else if (pageCountForGetGroups > 1) {
        getDropdownListForGroups(pageCountForGetGroups);
      }
    }
  }, [
    dropdownSearchForGetGroups,
    pageCountForGetGroups,
    resetFlagForGetGroups,
  ]);

  React.useEffect(() => {
    if (resetFlagForGetGroups) {
      resetListForGroups();
    }
  }, [resetFlagForGetGroups]);

  const resetListForGroups = () => {
    setDropdownSearchForGetGroups("");
    setPageCountForGetGroups(1);
    setTotalGroupsCount(0);
    setHasMoreGroups(true);
    getDropdownListForGroups(1, true);
  };

  // IF SEARCH VALUE THEN WE ARE GIVEN SOME TIMER
  const handleSearchedValueChangeForGroups = (data: any) => {
    if (timer) {
      clearTimeout(timer); // Clear the previous timer
    }
    // Set a new timer with your desired delay (e.g., 500ms)
    const newTimer = setTimeout(() => {
      setDropdownSearchForGetGroups(data);
    }, 500); // Adjust the delay as needed (in milliseconds)

    setTimer(newTimer); // Store the timer ID for cleanup
  };

  // GET DROPDOWN LIST FOR GROUPS
  const getDropdownListForGroups = async (pageCount: any, reset = false) => {
    // Don't fetch if we've reached the end
    if (!hasMoreGroups && !reset) {
      return;
    }

    dispatch(showLoader());
    try {
      const response = await getLazyLoadingApiResponse(
        "task/groups",
        pageCount,
        6,
      );
      const groupsList = response?.data?.taskGroups || [];
      const total = response?.data?.total || 0;

      // Update total count and check if we have more data
      setTotalGroupsCount(total);
      const currentLoadedCount = reset ? 0 : dropdownOptionsForGetGroups.length;
      setHasMoreGroups(currentLoadedCount + groupsList.length < total);

      const mappedGroupData = groupsList?.map((group: any) => ({
        label: `${group?.name} (Group)`,
        value: `${group?.id}`,
      }));

      if (mappedGroupData && mappedGroupData.length > 0) {
        if (reset) {
          setDropdownOptionsForGetGroups(mappedGroupData);
          setResetFlagForGetGroups(false);
        } else if (!dropdownOptionsForGetGroups.includes(mappedGroupData[0])) {
          let options = dropdownOptionsForGetGroups.concat(mappedGroupData);
          setDropdownOptionsForGetGroups(options);
        }
      }
    } catch (error) {
      console.log("Error fetching groups list:", error);
    } finally {
      dispatch(hideLoader());
    }
  };

  // GET DROPDOWN SEARCH LIST FOR GROUPS
  const getDropdownSearchListForGroups = async (search: string) => {
    try {
      const response = await getLazyLoadingSearchApiResponse(
        "task/groups",
        search,
        "searchKey",
      );
      const groupsList = response?.data?.taskGroups || [];
      const total = response?.data?.total || 0;

      // Reset pagination state for search
      setTotalGroupsCount(total);
      setHasMoreGroups(groupsList.length < total);

      const mappedGroupData = groupsList?.map((group: any) => ({
        label: `${group?.name} (Group)`,
        value: `${group?.id}`,
      }));
      if (mappedGroupData?.length) {
        setDropdownOptionsForGetGroups(mappedGroupData);
      }
    } catch (error) {
      console.log("Error fetching search groups list:", error);
    }
  };

  // **************************************************************
  //GET GROUPS LOGIC END

  // Get the appropriate dropdown options based on selected category
  const getAssignToOptions = React.useMemo(() => {
    // Always return the unified dropdown options for the current category
    return dropdownOptionsForAssignToList;
  }, [dropdownOptionsForAssignToList]);

  // No longer combining data - each category shows its own data

  return (
    <Formik
      initialValues={{
        [ADD_TASK_DETAILS.TASK_TYPE]: `${defaultSelectedData?.rowData?.taskType || ""}`,
        // ROLE_BASED_REMOVED (pre-Jamboree): dropped `|| rowData?.roleId`.
        [ADD_TASK_DETAILS.ASSIGN_TO]: `${defaultSelectedData?.rowData?.assigenTo || defaultSelectedData?.rowData?.assignedToAdmin || defaultSelectedData?.rowData?.groupId || ""}`,
        [ADD_TASK_DETAILS.DUE_DATE]: (() => {
          const existing = defaultSelectedData?.dueDate;
          // New task (no due date) → default to today. An existing due date
          // (including a future one) is kept as-is.
          if (!existing) {
            return moment().format("MM/DD/YYYY");
          }
          return moment.utc(existing).format("MM/DD/YYYY");
        })(),
        [ADD_TASK_DETAILS.STATUS]: `${defaultSelectedData?.status ? (defaultSelectedData?.status === "Pending" ? "pending" : defaultSelectedData?.status === "In Progress" ? "inProgress" : defaultSelectedData?.status === "Completed" ? "completed" : defaultSelectedData?.status) : ""}`,
        [ADD_TASK_DETAILS.TASK_DESCRIPTION]: `${
          defaultSelectedData?.rowData?.description || ""
        }`,
      }}
      validationSchema={validationSchema}
      enableReinitialize
      onSubmit={(values, { setSubmitting }) => {
        try {
          // Process the assign to field based on category and selected value
          const assignToValue = values[ADD_TASK_DETAILS.ASSIGN_TO];
          const selectedOption = getAssignToOptions?.find(
            (option: any) => option?.value === assignToValue,
          );

          // Create payload with appropriate field based on category and selection type
          const { [ADD_TASK_DETAILS.ASSIGN_TO]: removedField, ...restValues } =
            values;

          let processedValues = { ...restValues };

          // Add appropriate field based on category and selection
          if (assignToValue) {
            switch (assignToCategory) {
              case "individuals":
                processedValues.assignTo = assignToValue;
                break;
              case "groups":
                processedValues.groupId = assignToValue;
                break;
              // ROLE_BASED_REMOVED (pre-Jamboree):
              // case "role_based":
              //   processedValues.roleId = assignToValue;
              //   break;
              default:
                // Default to assignTo for any other category
                processedValues.assignTo = assignToValue;
            }
          }

          handleOnSubmittedData && handleOnSubmittedData(processedValues);
        } catch (error) {
          console.log(error);
        } finally {
          setSubmitting(false); // Reset submitting state
        }
      }}
    >
      {({
        errors,
        submitForm,
        isSubmitting,
        handleReset,
        isValid,
        values,
        touched,
        setFieldValue,
        handleBlur,
        handleSubmit,
      }) => {
        return (
          <Form
            onSubmit={handleSubmit}
            className="h-full flex flex-col justify-between rounded-lg"
          >
            <div className="w-full flex flex-col gap-6 rounded-lg">
              {/* PATIENT DETAILS */}
              <div>
                {/* <Heading title="Patient Details" customStyle={headerStyle} /> */}
                <div style={{}}>
                  <div style={{ ...commonGridStyle, ...twoColumnStyle }}>
                    {" "}
                    <EventEHRLazyDropdown                      label="Task Type"
                      dropdownIcon={true}
                      dropdownOptions={shownTaskTypeOptions}
                      dropdownSearch={taskTypeSearch}
                      selectedValue={
                        values[ADD_TASK_DETAILS.TASK_TYPE]
                          ? {
                              label: values[ADD_TASK_DETAILS.TASK_TYPE],
                              value: values[ADD_TASK_DETAILS.TASK_TYPE],
                            }
                          : null
                      }
                      filterOptions={(o: any) => o}
                      setDropdownSearch={(data: any) =>
                        handleTaskTypeSearch(data)
                      }
                      setPageCount={() => {}}
                      setResetFlag={() => {
                        setTaskTypeSearch("");
                        setTaskTypeSearchResults(null);
                      }}
                      setSelectedValue={(data: any) =>
                        setFieldValue(
                          ADD_TASK_DETAILS.TASK_TYPE,
                          data?.value ?? "",
                        )
                      }
                      onBlur={handleBlur}
                      error={Boolean(
                        touched[ADD_TASK_DETAILS.TASK_TYPE] &&
                          errors[ADD_TASK_DETAILS.TASK_TYPE],
                      )}
                      helperText={
                        touched[ADD_TASK_DETAILS.TASK_TYPE]
                          ? errors[ADD_TASK_DETAILS.TASK_TYPE]
                          : undefined
                      }
                    />
                    <SelectDropdown                      label="Assign To"
                      name="assignToCategory"
                      value={assignToCategory}
                      options={assignToCategoryOptions}
                      onChangeCb={(e) => {
                        setAssignToCategory(e?.target?.value);
                        // Reset the selected value when category changes
                        setSelectedAssignToValue(null);
                        setFieldValue(ADD_TASK_DETAILS.ASSIGN_TO, "");
                        // Clear the dropdown options for the new category
                        setDropdownOptionsForAssignToList([]);
                        setResetFlagForAssignToList(true);
                        // Reset pagination and search
                        setPageCountForAssignToList(1);
                        setDropdownSearchForAssignToList("");
                      }}
                      onBlur={handleBlur}
                      error={Boolean(
                        !assignToCategory &&
                          touched[ADD_TASK_DETAILS.ASSIGN_TO] &&
                          errors[ADD_TASK_DETAILS.ASSIGN_TO],
                      )}
                      helperText={
                        !assignToCategory && touched[ADD_TASK_DETAILS.ASSIGN_TO]
                          ? "Assign to is required"
                          : undefined
                      }
                    />
                  </div>

                  <div style={{ ...commonGridStyle, ...twoColumnStyle }}>
                    {assignToCategory && (
                      <>
                        {assignToCategory === "individuals" && (
                          <EventEHRLazyDropdown
                            label="Select Individual"
                            onBlur={handleBlur}
                            dropdownIcon={true}
                            filterOptions={(options) => options}                            error={
                              assignToCategory === "individuals" &&
                              touched[ADD_TASK_DETAILS.ASSIGN_TO] &&
                              Boolean(errors[ADD_TASK_DETAILS.ASSIGN_TO])
                            }
                            helperText={
                              assignToCategory === "individuals" &&
                              touched[ADD_TASK_DETAILS.ASSIGN_TO]
                                ? "Individual is required"
                                : undefined
                            }
                            dropdownOptions={dropdownOptionsForAssignToList}
                            dropdownSearch={dropdownSearchForAssignToList}
                            selectedValue={selectedAssignToValue}
                            setDropdownSearch={
                              handleSearchedValueChangeForAssignToList
                            }
                            setPageCount={setPageCountForAssignToList}
                            setResetFlag={setResetFlagForAssignToList}
                            setSelectedValue={(data) => {
                              setFieldValue(
                                ADD_TASK_DETAILS.ASSIGN_TO,
                                data?.value ? data?.value : "",
                              );
                              setSelectedAssignToValue(data);
                            }}
                          />
                        )}

                        {assignToCategory === "groups" && (
                          <EventEHRLazyDropdown
                            label="Select Group"
                            onBlur={handleBlur}
                            dropdownIcon={true}
                            filterOptions={(options) => options}                            error={
                              assignToCategory === "groups" &&
                              touched[ADD_TASK_DETAILS.ASSIGN_TO] &&
                              Boolean(errors[ADD_TASK_DETAILS.ASSIGN_TO])
                            }
                            helperText={
                              assignToCategory === "groups" &&
                              touched[ADD_TASK_DETAILS.ASSIGN_TO]
                                ? "Group is required"
                                : undefined
                            }
                            dropdownOptions={dropdownOptionsForAssignToList}
                            dropdownSearch={dropdownSearchForAssignToList}
                            selectedValue={selectedAssignToValue}
                            setDropdownSearch={
                              handleSearchedValueChangeForAssignToList
                            }
                            setPageCount={setPageCountForAssignToList}
                            setResetFlag={setResetFlagForAssignToList}
                            setSelectedValue={(data) => {
                              setFieldValue(
                                ADD_TASK_DETAILS.ASSIGN_TO,
                                data?.value ? data?.value : "",
                              );
                              setSelectedAssignToValue(data);
                            }}
                          />
                        )}

                        {/* ROLE_BASED_REMOVED (pre-Jamboree): restore to
                            re-enable role assignment.
                        {assignToCategory === "role_based" && (
                          <EventEHRLazyDropdown
                            label="Select Role"
                            onBlur={handleBlur}
                            dropdownIcon={true}
                            filterOptions={(options) => options}                            error={
                              assignToCategory === "role_based" &&
                              touched[ADD_TASK_DETAILS.ASSIGN_TO] &&
                              Boolean(errors[ADD_TASK_DETAILS.ASSIGN_TO])
                            }
                            helperText={
                              assignToCategory === "role_based" &&
                              touched[ADD_TASK_DETAILS.ASSIGN_TO]
                                ? "Role is required"
                                : undefined
                            }
                            dropdownOptions={dropdownOptionsForAssignToList}
                            dropdownSearch={dropdownSearchForAssignToList}
                            selectedValue={selectedAssignToValue}
                            setDropdownSearch={
                              handleSearchedValueChangeForAssignToList
                            }
                            setPageCount={() => {}}
                            setResetFlag={setResetFlagForAssignToList}
                            setSelectedValue={(data) => {
                              setFieldValue(
                                ADD_TASK_DETAILS.ASSIGN_TO,
                                data?.value ? data?.value : "",
                              );
                              setSelectedAssignToValue(data);
                            }}
                          />
                        )}
                        */}
                      </>
                    )}

                    <DatePicker
                      name={ADD_TASK_DETAILS.DUE_DATE}
                      value={values[ADD_TASK_DETAILS.DUE_DATE]}
                      onChangeCb={(value) => {
                        setFieldValue(ADD_TASK_DETAILS.DUE_DATE, value);
                      }}                      // isStarPresent={true}
                      label="Due Date"
                      style={{ width: "100%" }}
                      minDate={moment().startOf("day")} // Use the start of the day
                      error={
                        touched[ADD_TASK_DETAILS.DUE_DATE] &&
                        Boolean(errors[ADD_TASK_DETAILS.DUE_DATE])
                      }
                      helperText={
                        touched[ADD_TASK_DETAILS.DUE_DATE]
                          ? errors[ADD_TASK_DETAILS.DUE_DATE]
                          : undefined
                      }
                    />

                    <SelectDropdown
                      label="Status"
                      name={ADD_TASK_DETAILS.STATUS}
                      value={values[ADD_TASK_DETAILS.STATUS]}
                      options={STATUS_FOR_TASK}
                      onChangeCb={(e) =>
                        setFieldValue(ADD_TASK_DETAILS.STATUS, e.target.value)
                      }
                      onBlur={handleBlur}
                      error={Boolean(
                        touched[ADD_TASK_DETAILS.STATUS] &&
                          errors[ADD_TASK_DETAILS.STATUS],
                      )}
                      helperText={
                        touched[ADD_TASK_DETAILS.STATUS]
                          ? errors[ADD_TASK_DETAILS.STATUS]
                          : undefined
                      }
                    />
                  </div>

                  <div style={{ ...commonGridStyle, ...fullWidthStyle }}>
                    {" "}
                    <Input
                      // disabled
                      multiline={true}
                      rows={3}
                      maxWords={250}
                      textFieldLabel="Enter Task Description"
                      // isStarPresent={true}
                      name={ADD_TASK_DETAILS.TASK_DESCRIPTION}
                      value={values[ADD_TASK_DETAILS.TASK_DESCRIPTION]}
                      onChangeCb={(e) => {
                        setFieldValue(
                          ADD_TASK_DETAILS.TASK_DESCRIPTION,
                          e.target.value,
                        );
                      }}
                      onBlur={handleBlur}
                      size="small"
                      error={
                        touched[ADD_TASK_DETAILS.TASK_DESCRIPTION] &&
                        Boolean(errors[ADD_TASK_DETAILS.TASK_DESCRIPTION])
                      }
                      helperText={
                        touched[ADD_TASK_DETAILS.TASK_DESCRIPTION]
                          ? errors[ADD_TASK_DETAILS.TASK_DESCRIPTION]
                          : undefined
                      }
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-end mt-6 gap-4">
              <Button
                label={TEXT_CONSTANTS?.CANCEL}
                variant="outlined"
                onClickCb={() => {
                  if (handleClose) {
                    handleClose(); // Call the handleClose function if it's defined
                  }
                }}
                style={{
                  width: "120px",
                  height: "44px",
                  padding: "12px 20px",
                  fontSize: "16px",
                  backgroundColor: "white",
                  border: "none",
                  color: "#393939",
                }}
              />
              <Button
                // disabled={!isValid} // Button disabled if form is invalid or submitting
                type="submit"                label={TEXT_CONSTANTS?.SAVE}
                variant="contained"
                onClickCb={() => {}}
                style={{
                  width: "120px",
                  height: "44px",
                  padding: "12px 20px",
                  fontSize: "16px",
                }}
              ></Button>
            </div>
          </Form>
        );
      }}
    </Formik>
  );
};

// ADD TASK DIALOG

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialogContent-root": {
    padding: "16px 24px",
  },
  "& .MuiDialogActions-root": {
    padding: theme.spacing(1),
  },
  "& .MuiPaper-root": {
    BorderAllRounded: "8px",
    Height: "436px",
    width: "708px", // Set your custom width here
    maxWidth: "none", // Prevents Material-UI from overriding your width
  },
}));

interface AddTasksDialogProps {
  isOpen: boolean;
  dialogHeading: string;
  defaultTaskData?: any;
  handleClose: () => void;
  handleAddTaskData: (data: any) => void;
}

export default function AddTasksDialog({
  isOpen = false,
  defaultTaskData,
  handleClose,
  handleAddTaskData,
  dialogHeading = "Your Heading Here...",
}: AddTasksDialogProps) {
  return (
    <React.Fragment>
      <BootstrapDialog
        onClose={(event, reason) => {
          if (reason !== "backdropClick") {
            handleClose();
          }
        }}
        aria-labelledby="customized-dialog-title"
        open={isOpen}
      >
        <div
          className="flex justify-between items-center rounded-lg"
          style={{ margin: "14px 24px" }}
        >
          <Heading title={dialogHeading} />
          <div onClick={handleClose} className="cursor-pointer">
            <CloseIcon />
          </div>
        </div>

        <DialogContent
          dividers
          // style={{ padding: "24px 16PX", borderRadius: "8px" }}
        >
          <AddTask
            defaultSelectedData={defaultTaskData}
            handleOnSubmittedData={(formData) => {
              handleAddTaskData(formData);
            }}
            handleClose={handleClose}
          />
        </DialogContent>
      </BootstrapDialog>
    </React.Fragment>
  );
}
